<template>
    <span v-if="isCompoundRx">
        <b-dropdown v-if="!isLoadingPrinters" :text="prettyPrinterName(selectedPrinter)" variant="outline-primary" size="sm">
            <b-dropdown-item-button :active="!selectedPrinter"
                @click="selectedPrinter = null">
                <i class="fa fa-building"></i>
                Store Default Printer
            </b-dropdown-item-button>
            <b-dropdown-item-button v-for="p in localPrinters" :key="p.name"
                :active="p.name === selectedPrinter?.name" @click="selectedPrinter = p">
                <i class="fa fa-print"></i>
                {{ prettyPrinterName(p) }}
                <div class="d-flex" style="gap: 6px;">
                    <small v-if="p.default">Local Default</small>
                    <small v-if="p.status !== 'Unknown'">{{ p.status }}</small>
                </div>
            </b-dropdown-item-button>
            <b-dropdown-item disabled v-if="!hasLocalPrinters">
                <i class="fa fa-info-circle" />
                To print to local printers, install<br>the Scriptly Client Service
            </b-dropdown-item>
        </b-dropdown>
        <b-button variant="info" @click="printCompoundIngredients" :disabled="isPrintingIngredients" size="sm"
            v-b-tooltip.hover.ds750
            title="Prints the compound preparation worksheet for this prescription — each ingredient and its quantity. Used at the bench during compounding.">
            <i class="fa fa-flask"></i> Print Ingredients
        </b-button>
        <b-button variant="info" @click="printFormulaWorksheet" :disabled="isPrintingWorksheet" size="sm"
            v-b-tooltip.hover.ds750
            title="Prints the formula worksheet as the compounding record for this prescription — the lot numbers and expiration dates of the ingredients used, the beyond-use date, and who compounded and verified it. Prints blank if no batch record is found.">
            <i class="fa fa-clipboard-list"></i> Print Formula Worksheet
        </b-button>
    </span>
</template>

<script lang="ts">
// Compound-printing UI cluster, extracted from PrescriptionDetailsPage: the local-printer
// dropdown (Scriptly client discovery) and the "Print Ingredients" worksheet button. Self-
// contained for behavior — a read-only prescription prop in, no events out — and it delegates
// its print work to PrintService. Button spacing is the host row's (`.rx-actions`), not local.
//
// Printing behavior is ported verbatim: PrintService keeps its absolute localhost:7274 URLs
// (bypassing axios baseURL), owns the auth-token/job-id/local-print orchestration, and silently
// falls back to queue printing when the local client is missing (getDevices returns []). This
// cluster has no watchers.
import { Component, Vue, Prop } from "vue-property-decorator";
import { AxiosError } from "axios";
import { Prescription } from "@/models/Prescription";
import { DrugSource } from "@/models/Drug/DrugProduct";
import { PrintService } from "@/services/PrintService";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

@Component
export default class CompoundPrintActions extends Vue {
    @Prop({ required: true }) prescription!: Prescription;

    private isPrintingIngredients = false;
    private isPrintingWorksheet = false;
    private localPrinters: any[] = [];
    private selectedPrinter: any | null = null;
    private isLoadingPrinters = false;

    // Rendered only for a saved compound Rx (drugSource === CompoundProduct).
    get isCompoundRx(): boolean {
        return !!(
            this.prescription &&
            this.prescription.rxNumber &&
            this.prescription.drugSource === DrugSource.CompoundProduct
        );
    }

    mounted() {
        // Printer discovery only matters when the compound cluster actually renders. Gating on
        // isCompoundRx avoids a doomed localhost:7274 request on every non-compound Rx page.
        if (this.isCompoundRx) {
            this.loadLocalPrinters();
        }
    }

    async loadLocalPrinters() {
        try {
            this.isLoadingPrinters = true;
            // PrintService.getDevices swallows a missing/stopped client and returns [],
            // so a failed discovery silently defaults to queue printing.
            this.localPrinters = await PrintService.getDevices();

            // Restore last selected printer from localStorage
            const lastPrinter = localStorage.getItem("active-ingredients-printer");
            if (lastPrinter) {
                this.selectedPrinter = this.localPrinters?.find(p => p.name === lastPrinter);
            }
        } finally {
            this.isLoadingPrinters = false;
        }
    }

    get hasLocalPrinters() {
        return !!this.localPrinters.length;
    }

    prettyPrinterName(printer: any) {
        return PrintService.prettyPrinterName(printer);
    }

    get rxId(): string {
        return `${this.prescription.storeID}-${this.prescription.rxNumber}-${this.prescription.rfNumber}`;
    }

    async printCompoundIngredients() {
        try {
            this.isPrintingIngredients = true;
            await this.print(
                localPrint => PrintService.getCompoundIngredients(this.rxId, localPrint),
                'Compound ingredients');
        } finally {
            this.isPrintingIngredients = false;
        }
    }

    async printFormulaWorksheet() {
        try {
            this.isPrintingWorksheet = true;
            await this.print(
                localPrint => PrintService.getFormulaWorksheet(this.rxId, localPrint),
                'Formula worksheet');
        } finally {
            this.isPrintingWorksheet = false;
        }
    }

    /**
     * Shared local-versus-queue orchestration for the compound print actions. Both documents go
     * through the same printer selection, so they share one stored printer preference.
     */
    private async print(
        request: (localPrint: boolean) => Promise<{ toPrint?: any[] }>,
        documentName: string) {
        try {
            const localPrint = !!this.selectedPrinter;
            const result = await request(localPrint);

            if (localPrint && result.toPrint?.length) {
                await PrintService.sendToLocalPrint({
                    printer: this.selectedPrinter,
                    jobs: result.toPrint,
                    // baseURL has its trailing /api stripped for the local client.
                    apiBaseUrl: this.$http.defaults.baseURL?.replace("/api", ""),
                    authProviderType: (this as any).$authProviderType,
                    storageKey: "active-ingredients-printer",
                });
            }

            this.$notification(NotificationOptions.notificationOptionsPreset(
                localPrint
                    ? `${documentName} sent to local printer successfully`
                    : `${documentName} sent to print queue successfully`,
                NotificationOptions.NotificationTypes.success
            ));
        } catch (err) {
            const error = err as AxiosError;
            console.error(`Error printing ${documentName.toLowerCase()}:`, error);
            this.$notification(NotificationOptions.error(error));
        }
    }
}
</script>
